import React, { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
    Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from './ui/command';

const MAX_RESULTS = 500;

export const CommessaCombobox = ({ commesse, value, onChange, placeholder = 'Seleziona commessa', testid }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    const selected = useMemo(() => commesse.find(c => c.id === value), [commesse, value]);

    const results = useMemo(() => {
        const term = query.trim().toLowerCase();
        const base = term
            ? commesse.filter(c =>
                (c.number || '').toLowerCase().includes(term) ||
                (c.telaio || '').toLowerCase().includes(term) ||
                (c.client_name || '').toLowerCase().includes(term) ||
                (c.mod || '').toLowerCase().includes(term))
            : commesse;
        return base.slice(0, MAX_RESULTS);
    }, [commesse, query]);

    const label = selected
        ? `${selected.number}${selected.telaio ? ` · ${selected.telaio}` : ''}`
        : placeholder;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid={testid}>
                    <span className="truncate">{label}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                <Command shouldFilter={false}>
                    <CommandInput placeholder="Cerca commessa / telaio..." value={query} onValueChange={setQuery} />
                    <CommandList>
                        <CommandEmpty>Nessuna commessa disponibile</CommandEmpty>
                        <CommandGroup>
                            {value && (
                                <CommandItem value="__none__" onSelect={() => { onChange(''); setOpen(false); }} data-testid="commessa-option-none">
                                    <span className="text-muted-foreground">— Nessuna commessa —</span>
                                </CommandItem>
                            )}
                            {results.map(c => (
                                <CommandItem
                                    key={c.id}
                                    value={c.id}
                                    onSelect={() => { onChange(c.id); setOpen(false); }}
                                    data-testid={`commessa-option-${c.id}`}
                                >
                                    <Check className={cn('mr-2 h-4 w-4', value === c.id ? 'opacity-100' : 'opacity-0')} />
                                    <span className="font-medium mr-2 shrink-0">{c.number}</span>
                                    <span className="truncate flex-1 font-mono text-xs">{c.telaio || 'no telaio'}</span>
                                    {c.status === 'associata' && (
                                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-700 shrink-0">associata</span>
                                    )}
                                    {c.client_name && <span className="ml-2 text-xs text-muted-foreground shrink-0 truncate max-w-[120px]">{c.client_name}</span>}
                                </CommandItem>
                            ))}
                            {results.length >= MAX_RESULTS && (
                                <div className="px-3 py-2 text-xs text-muted-foreground">Troppi risultati: affina la ricerca…</div>
                            )}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

export default CommessaCombobox;
